import { PianoHingeRenderer, SupplyPartRenderer } from "@project/emerge";
import type { Blueprint } from "@project/emerge-types";
import type React from "react";
import type { Group } from "three";
import { LTPartType } from "../enum/PartType";
import { AddableRenderer } from "./AddableRenderer";
import { AngleSectionRenderer } from "./AngleSectionRenderer";
import { FligthcaseBlueprintRenderer } from "./FligthcaseBlueprintRenderer";
import { MeasurementRenderer } from "./MeasurementRenderer";
import { PadRenderer } from "./PadRenderer";
import { PanelRenderer } from "./PanelRenderer";
import { ProfileRenderer } from "./ProfileRenderer";
import { RackRailRenderer } from "./RackRailRenderer";
import { RunnerRenderer } from "./RunnerRenderer";
import { SupplyPartRendererBling } from "./SupplyPartRendererBling";
import { VolumeRenderer } from "./VolumeRenderer";

// A simple function type alias that (in practice) is more lenient than React.FC
type CovariantFC<P> = (props: P) => React.ReactElement;

// Neue gemeinsame Schnittstelle für alle Part-Renderer
export type PartRenderer = CovariantFC<{ id: string }>;

export type BlueprintRenderer<T extends Blueprint = Blueprint> = CovariantFC<{
  blueprint: T;
}>;

export type MeasurementRendererType = CovariantFC<{
  target?: React.RefObject<Group | null> | null;
  visible?: boolean;
  onBoundingBoxCalculated?: () => void;
  onBoundingBoxReady?: (boundingBox: {
    x: number;
    y: number;
    z: number;
  }) => void;
  pdfPreview?: boolean;
}>;

// Aktualisierte Rendererregistrierung, die jetzt ID-basierte Renderer verwendet
export const partRenderers: Record<string, PartRenderer> = {
  [LTPartType.Panel]: PanelRenderer as PartRenderer,
  [LTPartType.TrayPanel]: PanelRenderer as PartRenderer,
  [LTPartType.Profile]: ProfileRenderer as PartRenderer,
  [LTPartType.AngleSection]: AngleSectionRenderer as PartRenderer,
  [LTPartType.Corner]: SupplyPartRendererBling as PartRenderer,
  [LTPartType.Roller]: SupplyPartRenderer as PartRenderer,
  [LTPartType.RackRail]: RackRailRenderer as PartRenderer,
  [LTPartType.SlideRail]: RunnerRenderer as PartRenderer,
  [LTPartType.Pad]: PadRenderer as PartRenderer,
  [LTPartType.Volume]: VolumeRenderer as PartRenderer,
  [LTPartType.Dish]: AddableRenderer as PartRenderer,
  [LTPartType.Handle]: AddableRenderer as PartRenderer,
  [LTPartType.Foot]: AddableRenderer as PartRenderer,
  [LTPartType.TrolleyHandle]: AddableRenderer as PartRenderer,
  [LTPartType.Lock]: AddableRenderer as PartRenderer,
  [LTPartType.Measurements]: MeasurementRenderer as PartRenderer,
  [LTPartType.CornerBrace]: AddableRenderer as PartRenderer,
  [LTPartType.Hinge]: AddableRenderer as PartRenderer,
  [LTPartType.PianoHinge]: PianoHingeRenderer as PartRenderer,
};

export const blueprintRenderers: Record<string, BlueprintRenderer> = {
  haube: FligthcaseBlueprintRenderer as BlueprintRenderer,
  koffer: FligthcaseBlueprintRenderer as BlueprintRenderer,
  keyboardcase: FligthcaseBlueprintRenderer as BlueprintRenderer,
  truhe: FligthcaseBlueprintRenderer as BlueprintRenderer,
  case3d: FligthcaseBlueprintRenderer as BlueprintRenderer,
  lcase: FligthcaseBlueprintRenderer as BlueprintRenderer,
  singledoor: FligthcaseBlueprintRenderer as BlueprintRenderer,
  doubledoor: FligthcaseBlueprintRenderer as BlueprintRenderer,
};

export const measurementRenderer: MeasurementRendererType =
  MeasurementRenderer as MeasurementRendererType;
